import type { ComponentProps, PropsWithChildren } from 'react';

import { Translate, withI18nPlaceholders } from '@change-corgi/core/react/i18n';
import { InlineMessage } from '@change-corgi/design-system-legacy/components/alerts';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';
import { DesignSystemLegacy } from '@change-corgi/design-system-legacy/core';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { createModalHook } from '@change-corgi/design-system-legacy/modals';
import { sunriseTheme } from '@change-corgi/design-system-legacy/theme';
import { Text } from '@change-corgi/design-system-legacy/typography';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionGamma/petition-gamma.yml';

import {
	PetitionDetailsPageDataContextProvider,
	PetitionDetailsPageFcmContextProvider,
} from 'src/app/pages/petitionDetails/pageContext';
import { isError, isLoading } from 'src/app/shared/utils/async';

import { useApiDecisionMakers } from '../../../../../../hooks/decisionMakers';
import type { PetitionDetailsFcm } from '../../../../../../hooks/fcm';
import { DecisionMakerCardContainer } from '../../index';

const TranslationPlaceholdersProvider = withI18nPlaceholders(I18N_PLACEHOLDERS, ({ children }: PropsWithChildren) => (
	<>{children}</>
));

export const useDecisionMakerCardsModal = createModalHook<{
	pageData: ComponentProps<typeof PetitionDetailsPageDataContextProvider>['pageData'];
	fcm: PetitionDetailsFcm;
	isSunriseEnabled: boolean;
}>({
	name: 'DecisionMakersModal',
	title: () => (
		<Text size="huge">
			<Translate value="fe.pages.petition_new.decision_maker" />
		</Text>
	),
	body: function DecisionMakersModalBody({
		options: {
			pageData: {
				data: {
					petition: {
						slug,
						primaryDecisionMakerConnection: { totalCount },
						dmResponsesConnection: { nodes: allDmResponses },
					},
				},
			},
		},
	}) {
		const apiDmResult = useApiDecisionMakers(slug, totalCount);

		if (isLoading(apiDmResult)) return <Loader size={['s', 'm']} mx="auto" my={16} />;

		if (isError(apiDmResult))
			return (
				<InlineMessage size="large" variant="error">
					<Translate value="fe.errors.generic_try_again" />
				</InlineMessage>
			);

		return (
			<>
				{apiDmResult.decisionMakers.map((decisionMaker) => (
					// using role due to https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html
					// eslint-disable-next-line jsx-a11y/no-redundant-roles
					<ul role="list" key={decisionMaker.id}>
						<li>
							<Box mb={24}>
								<DecisionMakerCardContainer decisionMaker={decisionMaker} allDmResponses={allDmResponses} />
							</Box>
						</li>
					</ul>
				))}
			</>
		);
	},
	decorator: ({ children, options: { pageData, fcm, isSunriseEnabled } }) => {
		return (
			<PetitionDetailsPageDataContextProvider pageData={pageData}>
				<PetitionDetailsPageFcmContextProvider pageFcm={fcm}>
					<TranslationPlaceholdersProvider>
						<DesignSystemLegacy theme={isSunriseEnabled ? sunriseTheme : undefined}>{children}</DesignSystemLegacy>
					</TranslationPlaceholdersProvider>
				</PetitionDetailsPageFcmContextProvider>
			</PetitionDetailsPageDataContextProvider>
		);
	},
});
